
import { supabase } from '@/integrations/supabase/client';
import { createTemplate, detectSignatureFields } from './docuSealHandler';

export interface UploadedFile {
  id: string;
  filename: string;
  size: number;
  contentType: string;
  url: string;
  analyzed: boolean;
}

export interface SignedDocument {
  id: string;
  originalFileId: string;
  filename: string;
  size: number;
  contentType: string;
  url: string;
  signedAt: string;
}

/**
 * Upload file to Supabase storage
 */
export async function uploadFile(file: File, userId: string): Promise<UploadedFile | null> {
  try {
    console.log(`Uploading file: ${file.name}, size: ${file.size}, type: ${file.type}`);
    // Create path for the file: userId/randomId-filename
    const fileExt = file.name.split('.').pop();
    const randomId = Math.random().toString(36).substring(2, 12);
    const filePath = `${userId}/${randomId}-${file.name}`;
    
    // Upload file to storage
    const { data: uploadData, error: uploadError } = await supabase
      .storage
      .from('user_files')
      .upload(filePath, file, {
        cacheControl: '3600',
        upsert: false
      });
    
    if (uploadError) {
      console.error('Error uploading file:', uploadError);
      throw new Error(uploadError.message);
    }
    
    console.log('File uploaded successfully to storage:', filePath);
    
    // Get file URL
    const { data: urlData } = supabase
      .storage
      .from('user_files')
      .getPublicUrl(filePath);
    
    // Record file in database
    const { data: fileRecord, error: insertError } = await supabase
      .from('files')
      .insert({
        user_id: userId,
        bucket_id: 'user_files',
        storage_path: filePath,
        filename: file.name,
        content_type: file.type,
        size: file.size
      })
      .select('*')
      .single();
    
    if (insertError) {
      console.error('Error recording file in database:', insertError);
      throw new Error(insertError.message);
    }
    
    console.log('File record created in database:', fileRecord.id);
    
    return {
      id: fileRecord.id,
      filename: file.name,
      size: file.size,
      contentType: file.type,
      url: urlData.publicUrl,
      analyzed: false
    };
  } catch (error) {
    console.error('File upload failed:', error);
    return null;
  }
}

/**
 * Upload a file and create a DocuSeal template from it
 */
export async function uploadFileAsTemplate(
  file: File, 
  userId: string, 
  templateName?: string
): Promise<{file: UploadedFile | null, templateId: string | null}> {
  try {
    // First upload the file normally
    const uploadedFile = await uploadFile(file, userId);
    
    if (!uploadedFile) {
      throw new Error('Failed to upload file');
    }
    
    // Now create a DocuSeal template from it
    const name = templateName || file.name.replace(/\.[^.]+$/, '');
    const template = await createTemplate(name, uploadedFile.id, userId);
    
    if (!template) {
      return {
        file: uploadedFile,
        templateId: null
      };
    }
    
    return {
      file: uploadedFile,
      templateId: template.id
    };
  } catch (error) {
    console.error('Error in uploadFileAsTemplate:', error);
    return {
      file: null,
      templateId: null
    };
  }
}

/**
 * Upload a company logo to Supabase storage
 */
export async function uploadCompanyLogo(file: File, userId: string): Promise<string | null> {
  try {
    // Create a specific folder for company logos
    const fileExt = file.name.split('.').pop();
    const filePath = `${userId}/company_logo.${fileExt}`;
    
    // Upload file to storage
    const { error: uploadError } = await supabase
      .storage
      .from('user_files')
      .upload(filePath, file, {
        cacheControl: '3600',
        upsert: true // Override previous logo
      });
    
    if (uploadError) {
      console.error('Error uploading company logo:', uploadError);
      throw new Error(uploadError.message);
    }
    
    // Get file URL
    const { data: urlData } = supabase
      .storage
      .from('user_files')
      .getPublicUrl(filePath);
    
    return urlData.publicUrl;
  } catch (error) {
    console.error('Logo upload failed:', error);
    return null;
  }
}

/**
 * Create a signing link for a document
 */
export function getDocumentSigningLink(fileId: string): string {
  return `/sign-document?fileId=${fileId}`;
}

/**
 * Create a DocuSeal signing link
 */
export async function getDocuSealSigningLink(
  fileId: string, 
  userId: string, 
  recipientEmail: string,
  recipientName?: string
): Promise<string | null> {
  try {
    // First check if template already exists for this file using our edge function
    const { data: templateData, error: templateError } = await supabase.functions.invoke('rpc-helpers', {
      body: {
        action: 'get_docuseal_template_by_file',
        data: {
          file_id: fileId,
          user_id: userId
        }
      }
    });
      
    let templateId = templateData?.docuseal_id;
    
    // If no template exists, create one
    if (!templateId) {
      // Get file data first
      const { data: fileData, error: fileError } = await supabase
        .from('files')
        .select('*')
        .eq('id', fileId)
        .eq('user_id', userId)
        .single();
        
      if (fileError || !fileData) {
        console.error('Error getting file data:', fileError);
        throw new Error('File not found');
      }
      
      // Create template from file
      const template = await createTemplate(
        fileData.filename.replace(/\.[^.]+$/, ''), 
        fileId, 
        userId,
        detectSignatureFields(fileId, fileData)
      );
      
      if (!template) {
        throw new Error('Failed to create template');
      }
      
      templateId = template.id;
    }
    
    // Create a submission for the template
    const submitters = [{
      name: recipientName || recipientEmail,
      email: recipientEmail
    }];
    
    const { data: submissionData, error: submissionError } = await supabase.functions.invoke('docuseal', {
      body: {
        action: 'createSubmission',
        data: {
          templateId,
          submitters
        }
      }
    });
    
    if (submissionError || !submissionData) {
      console.error('Error creating submission:', submissionError);
      throw new Error('Failed to create submission');
    }
    
    // For simplicity, we're assuming the first submitter is the one we want
    const submitter = submissionData.submitters[0];
    
    // Return the signing URL
    return submitter.submission_url || null;
  } catch (error) {
    console.error('Error in getDocuSealSigningLink:', error);
    return null;
  }
}

/**
 * Get signed documents for a user
 */
export async function getSignedDocuments(userId: string): Promise<SignedDocument[]> {
  try {
    // Use explicit type casting to tell TypeScript about the structure
    const { data, error } = await supabase
      .from('signed_documents')
      .select('*')
      .eq('user_id', userId)
      .order('signed_at', { ascending: false });
    
    if (error) {
      console.error('Error fetching signed documents:', error);
      throw error;
    }
    
    if (!data) return [];
    
    // Map the returned data to our SignedDocument interface
    return data.map(doc => {
      const { data: urlData } = supabase
        .storage
        .from('user_files')
        .getPublicUrl(doc.storage_path);
      
      return {
        id: doc.id,
        originalFileId: doc.original_file_id,
        filename: doc.filename,
        size: doc.size,
        contentType: doc.content_type,
        url: urlData.publicUrl,
        signedAt: doc.signed_at
      };
    });
  } catch (error) {
    console.error('Error getting signed documents:', error);
    return [];
  }
}

/**
 * Get DocuSeal signed documents for a user
 */
export async function getDocuSealSignedDocuments(userId: string): Promise<any[]> {
  try {
    // First get all templates for this user using our edge function
    const { data: templates, error: templatesError } = await supabase.functions.invoke('rpc-helpers', {
      body: {
        action: 'get_user_docuseal_templates',
        data: {
          user_id: userId
        }
      }
    });
      
    if (templatesError) {
      console.error('Error getting templates:', templatesError);
      return [];
    }
    
    if (!templates || !Array.isArray(templates) || templates.length === 0) {
      return [];
    }
    
    // Get all completed submissions for these templates
    const signedDocuments = [];
    
    for (const template of templates) {
      try {
        const { data: submissions, error: submissionsError } = await supabase.functions.invoke('docuseal', {
          body: {
            action: 'listSubmissions',
            data: {
              templateId: template.docuseal_id
            }
          }
        });
        
        if (submissionsError) {
          console.error(`Error getting submissions for template ${template.docuseal_id}:`, submissionsError);
          continue;
        }
        
        // Filter for completed submissions only
        const completedSubmissions = submissions.filter(s => s.status === 'completed' && s.result_document_url);
        
        signedDocuments.push(...completedSubmissions.map(s => ({
          ...s,
          templateName: template.name
        })));
      } catch (error) {
        console.error(`Error processing template ${template.docuseal_id}:`, error);
      }
    }
    
    return signedDocuments;
  } catch (error) {
    console.error('Error in getDocuSealSignedDocuments:', error);
    return [];
  }
}

/**
 * Analyze a document (e.g., PDF) using the analyze-document edge function
 */
export async function analyzeDocument(fileId: string): Promise<any> {
  try {
    console.log(`Analyzing document with fileId: ${fileId}`);
    
    const { data, error } = await supabase.functions.invoke('analyze-document', {
      body: { fileId }
    });
    
    if (error) {
      console.error('Error calling analyze-document function:', error);
      throw error;
    }
    
    if (!data) {
      console.error('No data returned from analyze-document function');
      throw new Error('No data returned from document analysis');
    }
    
    console.log('Document analysis completed successfully');
    return data;
  } catch (error: any) {
    console.error('Document analysis failed:', error);
    throw new Error(`Analysis failed: ${error.message || 'Unknown error'}`);
  }
}

/**
 * Get all uploaded files for a user
 */
export async function getUserFiles(userId: string): Promise<UploadedFile[]> {
  try {
    const { data, error } = await supabase
      .from('files')
      .select('*')
      .eq('user_id', userId)
      .order('created_at', { ascending: false });
    
    if (error) {
      console.error('Error fetching user files:', error);
      throw error;
    }
    
    return data.map(file => {
      const filePath = file.storage_path;
      const { data: urlData } = supabase
        .storage
        .from(file.bucket_id)
        .getPublicUrl(filePath);
      
      return {
        id: file.id,
        filename: file.filename,
        size: file.size,
        contentType: file.content_type,
        url: urlData.publicUrl,
        analyzed: file.analyzed
      };
    });
  } catch (error) {
    console.error('Error getting user files:', error);
    return [];
  }
}

/**
 * Delete a file
 */
export async function deleteFile(fileId: string, userId: string): Promise<boolean> {
  try {
    // Get file info
    const { data: fileData, error: fileError } = await supabase
      .from('files')
      .select('*')
      .eq('id', fileId)
      .eq('user_id', userId)
      .single();
    
    if (fileError) {
      console.error('Error getting file data:', fileError);
      return false;
    }
    
    // Delete from storage
    const { error: storageError } = await supabase
      .storage
      .from(fileData.bucket_id)
      .remove([fileData.storage_path]);
    
    if (storageError) {
      console.error('Error deleting file from storage:', storageError);
      return false;
    }
    
    // Delete from database
    const { error: dbError } = await supabase
      .from('files')
      .delete()
      .eq('id', fileId)
      .eq('user_id', userId);
    
    if (dbError) {
      console.error('Error deleting file record:', dbError);
      return false;
    }
    
    return true;
  } catch (error) {
    console.error('Error deleting file:', error);
    return false;
  }
}
